import { SelectProps, Tooltip } from 'antd';
import React from 'react';
import { flatMap } from 'lodash';

import { EVENT_NAME_WITH_BLOCK_RULE } from '@components/block-event-single/constant/constant';
import {
  IMappedStatusRowData,
  escapeRegExp,
  isMapped,
} from '@components/common/AutoCompleteInput/buildOptionsWithTag';
import {
  ItemOption,
  ItemOptionContent,
  StatusTag,
} from '@components/common/AutoCompleteInput/styled';
import AddEllipsisWithTooltip from '@components/common/EllipsisTooltip/AddEllipsisWithTooltip';
import { EStringOp } from '@src/emp-3.0/types';
import { categoryTypeMap } from '@src/emp-3.0/components/custom-dimension/custom-dimension-panel/constant';
import { useInitialInfoStore } from '../store/useInitialInfo';

export const buildOptionsWithHighlightAndTag = (
  options: SelectProps['options'] = [],
  searchText = '',
  mappedData?: IMappedStatusRowData[],
  blockedData?: string[],
) => {
  const escapeSearchText = escapeRegExp(searchText);
  const regex = new RegExp(`(${escapeSearchText})`, 'gi');
  const showBlockedSet = new Set(blockedData ?? []);

  return options.map((item) => {
    const { label, value, disabled: optionsItemDisabled, ...rest } = item;
    if (typeof label === 'string') {
      let labelContent: React.ReactNode = label;
      const showMapped = isMapped(label, mappedData);
      const showBlocked = showBlockedSet.has(label);
      let disabled = optionsItemDisabled || showBlocked;
      // Check for special cases
      if (EVENT_NAME_WITH_BLOCK_RULE.includes(label)) {
        disabled = false;
      }
      if (searchText !== '') {
        const parts = label.split(regex);
        labelContent = parts.map((part, index) =>
          regex.test(part) ? (
            <span key={index} style={{ fontWeight: 500, color: 'rgba(0,0,0,0.65)' }}>
              {part}
            </span>
          ) : (
            part
          ),
        );
      }

      return {
        value,
        label: (
          <ItemOption className={`${disabled ? 'disabled' : ''}`}>
            <ItemOptionContent>
              <AddEllipsisWithTooltip text={labelContent} tooltipText={label} dataIndex="name" />
            </ItemOptionContent>
            <div>
              {showMapped && <StatusTag>Mapped</StatusTag>}
              {showBlocked && <StatusTag>Blocked</StatusTag>}
            </div>
          </ItemOption>
        ),
        disabled,
        ...rest,
      };
    }
    return item;
  });
};
// START_EVENT = 'start_event',
// SUCCESS_EVENT = 'success_event',
// ERROR_EVENT = 'error_event',

const filterEvents = {
  // Metric:
  first_event: ['conviva_session_end', 'conviva_user_session_end'],
  second_event: ['conviva_session_start', 'conviva_user_session_start'],
  event: [
    'conviva_session_start',
    'conviva_user_session_start',
    'conviva_session_end',
    'conviva_user_session_end',
  ],
  // Flow:
  start_event: [
    'conviva_session_end',
    'conviva_user_session_end',
    'conviva_anr',
    '5xx server error',
    'app crash',
  ],
  success_event: [
    'conviva_session_start',
    'conviva_user_session_start',
    'conviva_session_end',
    'conviva_user_session_end',
    'conviva_anr',
    '5xx server error',
    'app crash',
  ],
  error_event: [
    'conviva_session_start',
    'conviva_user_session_start',
    'conviva_session_end',
    'conviva_user_session_end',
  ],
  without_event: [
    'conviva_session_start',
    'conviva_user_session_start',
    'conviva_session_end',
    'conviva_user_session_end',
  ],
};

let eventFilter: typeof filterEvents;
function getEventFilter() {
  const { userInfo } = useInitialInfoStore.getState();
  /**
   * HP-13429:
   * for customer c3.Wayfair-Test(1960188500) and its workbench account,
   * conviva_user_session_end and conviva_session_end can appear in complete event list for flow creation
   */
  if (userInfo?.customer_id && [1960188500, 1960189001].includes(userInfo?.customer_id)) {
    if (!eventFilter) {
      const userEventFilter = { ...filterEvents };
      userEventFilter.success_event = filterEvents.success_event.filter(
        (it) => !['conviva_session_end', 'conviva_user_session_end'].includes(it),
      );
      eventFilter = userEventFilter;
    }
    return eventFilter;
  }
  return filterEvents;
}

export const buildOptionsWithOutOfBox = (events) => {
  const mMap = new Map();

  (events ?? [])
    .filter((item) => item && item['event_id'] && item['event_name'] && item['event_category'])
    .forEach((item) => {
      const { event_id, event_name, event_category } = item;
      const newItem = { value: event_id, label: event_name };
      const currentArray = mMap.get(event_category) || [];
      mMap.set(event_category, [...currentArray, newItem]);
    });

  const initialEventOptions: SelectProps['options'] = [];
  const followUpEventOptions: SelectProps['options'] = [];
  const errorEventOptions: SelectProps['options'] = [];
  const eventFilter = getEventFilter();

  mMap.forEach((value, key) => {
    initialEventOptions.push({
      label: key,
      options:
        key === 'Custom'
          ? value.filter((item) => !eventFilter.start_event.includes(item.label))
          : value,
    });

    followUpEventOptions.push({
      label: key,
      options:
        key === 'Custom'
          ? value.filter((item) => !eventFilter.success_event.includes(item.label))
          : value,
    });

    errorEventOptions.push({
      label: key,
      options:
        key === 'Custom'
          ? value.filter((item) => !eventFilter.error_event.includes(item.label))
          : value,
    });
  });
  return { initialEventOptions, followUpEventOptions, errorEventOptions };
};
// management
export function generateSelectOptions(
  data: any[] | undefined,
  defaultValue: string = 'All',
): {
  createdByOptions: SelectProps['options'];
  updatedByOptions: SelectProps['options'];
  blockedByOptions: SelectProps['options'];
  categoryOptions: SelectProps['options'];
} {
  const flattenedData = flatMap(data ?? [], (element) => element.versions ?? [element]);

  const createdUserSet = new Set<string>();
  const updatedUserSet = new Set<string>();
  const blockedUserSet = new Set<string>();
  const categorySet = new Set<string>();

  flattenedData.forEach((item) => {
    item?.created_by && createdUserSet.add(item.created_by);
    item?.updated_by && updatedUserSet.add(item.updated_by);
    item?.block_by && blockedUserSet.add(item.block_by);
    if (item?.category) {
      categorySet.add(item.category);
    } else if (item?.event_category) {
      categorySet.add(item.event_category);
    }
  });

  const mapToOptions = (items: Set<string>): SelectProps['options'] => [
    { label: categoryTypeMap[defaultValue] ?? defaultValue, value: defaultValue },
    ...Array.from(items).map((item) => ({ label: categoryTypeMap[item] ?? item, value: item })),
  ];

  const createdByOptions = mapToOptions(createdUserSet);
  const updatedByOptions = mapToOptions(updatedUserSet);
  const blockedByOptions = mapToOptions(blockedUserSet);
  const categoryOptions = mapToOptions(categorySet);

  return { createdByOptions, updatedByOptions, blockedByOptions, categoryOptions };
}
// Deployment
export function getSelectiveDeploymentOptions(
  data: any[] | undefined,
  defaultValue: string = 'All',
): {
  typeOptions: SelectProps['options'];
  updatedByOptions: SelectProps['options'];
} {
  const typeSet = new Set<string>();
  const updatedUserSet = new Set<string>();

  const deploymentTypeMapping = {
    metrics: 'Metric',
    mapped_events: 'Mapped Event',
    data_transform: 'Dimension',
    custom_dimension: 'Dimension',
    flow: 'Flow',
  };

  data?.forEach((item) => {
    let type = item.type;

    // Combine 'data_transform' and 'custom_dimension' as 'Dimension'
    if (type === 'data_transform' || type === 'custom_dimension') {
      type = 'Dimension';
    }
    type && typeSet.add(type);
    item.last_updated_by && updatedUserSet.add(item.last_updated_by);
  });

  const typeOptions: SelectProps['options'] = [
    { label: defaultValue, value: defaultValue },
    ...Array.from(typeSet).map((type) => {
      return {
        label: deploymentTypeMapping[type] || type,
        value: type,
      };
    }),
  ];

  const updatedByOptions: SelectProps['options'] = [
    { label: defaultValue, value: defaultValue },
    ...Array.from(updatedUserSet).map((user) => ({ label: user, value: user })),
  ];

  return { typeOptions, updatedByOptions };
}

export const getOperatorOptions = () => {
  const operatorOptions = Object.values(EStringOp).map((value) => {
    let label =
      {
        [EStringOp.EQ]: 'equals',
        [EStringOp.NOT_EQ]: 'not equals',
      }[value] || value;
    let tooltipTitle = '';

    // add tooltip
    if (value === 'is not empty') {
      tooltipTitle = `Must have at least 1 character`;
    } else if (value === 'is empty') {
      tooltipTitle = `Matches "" or null values`;
    } else if (value === 'match pattern') {
      tooltipTitle = `Pattern match with * wildcard only. Ex: /profiles/v4/* matches /profiles/v4/123`;
    }

    if (tooltipTitle) {
      label = <Tooltip title={tooltipTitle}>{label}</Tooltip>;
    }
    return {
      value,
      label,
    };
  });
  const operatorOptionsExcludingNull = operatorOptions.filter(
    (option) => option.value !== EStringOp.IS_EMPTY && option.value !== EStringOp.IS_NOT_EMPTY,
  );

  return {
    operatorOptions,
    operatorOptionsExcludingNull,
  };
};

export const filterEventOptionsByEventType = (events, type) => {
  return events.filter((event) => !(getEventFilter()[type] ?? []).includes(event.name));
};
